import { forwardRef, useRef } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';

import { Box, useOutsideClick } from '@frontend/legacy-components';

import { useCascaderState } from '../context';
import {
  usePreventDropdownMousedownFocus,
  useVirtuosoSearchHighlightScroll,
} from '../hooks';
import {
  InternalSearchOption,
  InternalNoOptionsMessage,
  InternalSearchWrapper,
  InternalSearchOptionsList,
} from '../subcomponents';

const VirtualScroller = forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<{ style?: React.CSSProperties }>
>(function Scroller({ children, ...props }, ref) {
  return (
    <InternalSearchOptionsList
      {...props}
      listRef={ref as React.RefObject<HTMLUListElement>}
    >
      {children}
    </InternalSearchOptionsList>
  );
});

const ItemContent = (index: number) => <InternalSearchOption index={index} />;

const VirtualItemWrapper = forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<{ style?: React.CSSProperties }>
>(({ children, ...props }, ref) => {
  return (
    <Box as="li" {...props} ref={ref} width="var(--reference-width)">
      {children}
    </Box>
  );
});
VirtualItemWrapper.displayName = 'VirtualItemWrapper';

const virtuosoProps = {
  components: {
    Scroller: VirtualScroller,
    Item: VirtualItemWrapper,
  },
  itemContent: ItemContent,
};
/**
 * CascaderSearch is responsible for rendering the flattened search options of the Cascader,
 * as well as showing a message when no search options exist.
 */
export const CascaderSearch = () => {
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const {
    filteredOptions: options,
    highlightedValue,
    onRequestClose,
    setInputValue,
  } = useCascaderState();

  const { handleVirtuosoScrolling } = useVirtuosoSearchHighlightScroll(
    virtuosoRef,
    highlightedValue,
    options,
  );

  usePreventDropdownMousedownFocus(searchWrapperRef);

  useOutsideClick({
    ref: searchWrapperRef,
    handler: () => {
      setInputValue('');
      onRequestClose();
    },
  });

  return (
    <InternalSearchWrapper searchWrapperRef={searchWrapperRef}>
      <Virtuoso
        ref={virtuosoRef}
        data={options}
        {...virtuosoProps}
        isScrolling={handleVirtuosoScrolling}
        increaseViewportBy={2000}
        style={{
          height: `${Math.min(options.length * 46, 240)}px`,
          width: 'var(--reference-width)',
          overflowX: 'hidden',
        }}
        overscan={5}
      />
      {options.length === 0 && <InternalNoOptionsMessage />}
    </InternalSearchWrapper>
  );
};
